import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export type AdmittanceProps = {
  token: string;
  encryptionKey: string;
};

type WaitRoomProps = {
  sessionId: string;
  apiBaseUrl: string;
  onAdmitted: (admittance: AdmittanceProps) => void;
  onDismissed: () => void;
  onError: (message: string) => void;
};

type WaitRoomStatus = "checking" | "waiting";

type BaseNotification = {
  eventType: string;
  sessionId: string;
  participantId: string | undefined;
  token: string | undefined;
  encryptionKey: string | undefined;
};

const ADMITTANCE_TYPE = "DonorAdmittanceNotificationDto";
const DISMISSED_TYPE = "DonorDismissedFromWaitRoomNotificationDto";
const NO_SUCH_SESSION_TYPE = "NoSuchSessionNotificationDto";
const GENERIC_ERROR_TYPE = "GenericErrorNotificationDto";

export function WaitRoom({
  sessionId,
  apiBaseUrl,
  onAdmitted,
  onDismissed,
  onError,
}: WaitRoomProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_status, setStatus] = useState<WaitRoomStatus>("checking");
  const { t } = useTranslation();

  useEffect(
    () => {
      if (!sessionId) return;

      let cancelled = false;
      let ws: WebSocket | null = null;

      const openWebSocket = () => {
        if (cancelled) return;

        setStatus("waiting");

        try {
          // Reuse the same base host but switch http -> ws, https -> wss
          const wsBase = apiBaseUrl.replace(/^http/, "ws");
          const wsUrl = `${wsBase}/ws/workplace/sessions/${sessionId}/wait-room`;

          console.log(`Opening WS URL ${wsUrl}`);
          ws = new WebSocket(wsUrl);

          ws.onmessage = (event: MessageEvent) => {
            if (cancelled) return;

            let parsed: BaseNotification;
            try {
              parsed = JSON.parse(event.data);
            } catch (e) {
              console.warn("Failed to parse wait-room notification:", e);
              return;
            }

            const { eventType, participantId, token, encryptionKey } = parsed;

            if (eventType === NO_SUCH_SESSION_TYPE) {
              onError(t("errors.noSuchSession", "No such session"));
              return;
            }

            if (eventType === GENERIC_ERROR_TYPE) {
              onError(t("errors.genericErrorExtended"));
              return;
            }

            if (!participantId) {
              return;
            }

            if (eventType === ADMITTANCE_TYPE && token && encryptionKey) {
              onAdmitted({ token, encryptionKey });
              ws?.close();
            } else if (eventType == DISMISSED_TYPE) {
              onDismissed();
              ws?.close();
            }
          };

          ws.onerror = (evt) => {
            console.error("WaitRoom WebSocket error", evt);
            if (!cancelled) {
              onError(t("errors.genericErrorExtended"));
            }
          };

          ws.onclose = () => {
            ws = null;
          };
        } catch (err) {
          console.error("WaitRoom failed to open WebSocket", err);
          if (!cancelled) {
            onError(t("errors.genericErrorExtended"));
          }
        }
      };

      openWebSocket();

      return () => {
        cancelled = true;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBaseUrl, setStatus, sessionId]
  );

  return (
    <div
      id="wait-room"
      className="bg-blue-800/85 rounded card text-center mb-6 mt-3 wait-room"
    >
      <p className="text-xl mb-2">{t("waitRoom.heading")}</p>
      <p className="text-white font-thin text-sm">{t("waitRoom.text")}</p>
    </div>
  );
}
